/* eslint-disable i18next/no-literal-string -- the image downloader: cache names, HTTP headers, status and error codes, never text a user reads */
// Owner-pinned offline card images (offline plan W6, F22; contracts C0.3,
// C1b, C3, C5, C6 in mtg-api docs/superpowers/specs/2026-10-23-offline-contracts.md).
//
// State lives in two places:
// - The PIN RECORD, in the owner's fenced IndexedDB (store `meta`, key
//   META_IMAGES): the pinned URLs, the user's pause, a Cancel generation and
//   the image step's C1b backoff (Retry-After deadline). Every change is ONE
//   fencedWrite read-modify-write transaction, so it is serialized across
//   tabs by IndexedDB itself, aborts atomically when the C3 fence moved (a
//   stale writer's edit never lands, so nothing ever has to clean one up),
//   and C3 step 7 wipes it with the owner database.
// - The image BYTES, in Cache Storage, one cache per owner incarnation:
//   cb-img-<owner_dir>.<n> (offlineStore.ownerImageCacheName). C3 deletes
//   every incarnation's cache (verified); the orphan sweep keeps only
//   offline_owner's current one.
//
// Cache opens. caches.open() creates a missing cache, so the page opens an
// owner cache only through openOwnerCache: inside the SHARED cb-owner-write
// Web Lock (C3 step 2 takes it exclusive before it wipes), only after the
// gate passed, never creating on a read path, and re-validated against the
// storage fence afterwards on EVERY exit (a stale open whose incarnation is
// gone deletes the cache again). The service worker never opens one; it
// reads with caches.match(key, {cacheName}).
//
// Writers run under the C3 writer registry (offlineContext.beginWriter;
// aborted by C3 step 2 and by suspension). A put cannot be aborted, so a put
// whose capture went stale is undone: only its OWN entry (each put is tagged
// with its run id and deleted only when the tag still matches), and the
// whole cache only when that incarnation is gone.
//
// Scheduling (C1b). Pinning only writes the record and asks the shared
// scheduler for a run. The downloads are the scheduler's LAST step
// (runImageStep, registered by runtime.ts; the scheduler runs its steps
// under one browser-wide lock), which reads the persisted backoff first and
// stops at its first transient, persisting the new deadline (Retry-After as
// the floor, never shortened by a repin). A running downloader re-reads the
// record before each request and before keeping each put, so a Cancel,
// Remove or unpin in ANOTHER tab stops it too.
//
// Bytes come straight from the Scryfall CDN, never our API (/cards/images is
// 120 requests a minute per IP and never retried on a 429): at most
// MAX_CONCURRENCY at once, cache 'no-store' (the service worker passes those
// through, so they never enter its LRU). navigator.storage.estimate() is
// read before pinning (C5 rule 4).
//
// No browser global at module scope.
import { useSyncExternalStore } from 'react'
import { LOCK_OWNER_WRITE, META_IMAGES, STORE, TRIGGER } from './offline.codes'
import {
  fencedWrite, incarnationOf, mayWrite as mirrorMayWrite, ownerGet, ownerImageCacheName, readDeviceIndex, readStorageFence,
  type DeviceIndex, type FenceRecord, type WriterCtx,
} from './offlineStore'
import { beginWriter as registryBeginWriter, currentWriterCtx, type WriterHandle } from './offlineContext'
import { backoffDelayMs } from './schedulerCore'
import { requestSync } from './syncScheduler'

export type ImageSize = 'small' | 'normal'

/** Rough bytes per image, for the quota check (Scryfall JPEGs). */
export const BYTES_PER_IMAGE: Record<ImageSize, number> = { small: 20_000, normal: 110_000 }
export const MAX_CONCURRENCY = 6
export const IMAGE_TIMEOUT_MS = 15_000
/** How long Remove waits for a stopped run (C3 step 2's 5,000 ms). */
export const STOP_WAIT_MS = 5_000
/** Never fill the origin's quota past this share. */
export const QUOTA_HEADROOM = 0.9
export const PIN_SCHEMA = 1
/** The run that stored an entry (a stale run deletes only its own). */
export const RUN_HEADER = 'x-cb-image-run'

const IMAGE_HOST = 'cards.scryfall.io'
const SIZE_SEGMENT = /^\/(small|normal|large|png|art_crop|border_crop)\//

/** Why a run stopped. Names match C6 error codes where one exists. */
export type ImageDownloadError = 'storage_full' | 'server_error' | 'offline' | 'fenced'

/** The C6 `images` item (contract C6 states). */
export type C6ImagesState =
  | { state: 'not_started' }
  | { state: 'downloading'; percent: number }
  | { state: 'ready' }
  | { state: 'error'; code: ImageDownloadError | 'incomplete' }

export interface ImageDownloadStatus {
  ownerKey: string | null
  phase: 'idle' | 'downloading' | 'done' | 'cancelled' | 'error'
  total: number
  done: number
  failed: number
  bytes: number
  error: ImageDownloadError | null
  /** The server's Retry-After on a transient stop (null: none sent). */
  retryAfterMs: number | null
}

/** The persisted pin record (owner DB, store meta, key META_IMAGES). */
export interface PinRecord {
  schema_version: number
  urls: string[]
  paused: boolean
  /** Bumped by every Cancel and Remove: a downloader that started earlier stops. */
  cancel_gen: number
  /** C1b: the image step does nothing before this (ms since epoch). */
  retry_at: number
  backoff_n: number
}

export type PinResult =
  | { ok: true; queued: number; alreadyStored: number; estimatedBytes: number }
  | { ok: false; reason: 'offline' | 'storage_full' | 'fenced' | 'unsupported' | 'cancelled'; estimatedBytes?: number }

export interface OwnerImageStatus {
  pinned: number
  stored: number
  paused: boolean
  c6: C6ImagesState
}

/** Seams for tests. Every field defaults to the app's. */
export interface ImageDeps {
  caches?: CacheStorage | null
  fetch?: typeof fetch
  estimate?: (() => Promise<StorageEstimate>) | null
  isOnline?: () => boolean
  beginWriter?: () => WriterHandle | null
  mayWrite?: (ctx: WriterCtx) => boolean
  readFence?: (ownerKey: string) => Promise<FenceRecord | null>
  readIndex?: () => Promise<DeviceIndex>
  requestRun?: () => void
  sleep?: (ms: number) => Promise<void>
  now?: () => number
  jitter?: () => number
}

// --- names -------------------------------------------------------------------

export function imageUrlAtSize(url: string, size: ImageSize): string | null {
  let u: URL
  try { u = new URL(url) } catch { return null }
  if (u.protocol !== 'https:' || u.hostname !== IMAGE_HOST) return null
  if (!SIZE_SEGMENT.test(u.pathname)) return null
  u.pathname = u.pathname.replace(SIZE_SEGMENT, `/${size}/`)
  if (u.pathname.endsWith('.png')) u.pathname = u.pathname.replace(/\.png$/, '.jpg')
  return u.href
}

export function estimateBytes(count: number, size: ImageSize): number {
  return Math.max(0, count) * BYTES_PER_IMAGE[size]
}

function resolve(deps: ImageDeps) {
  const g = globalThis as typeof globalThis & { caches?: CacheStorage }
  const nav = typeof navigator !== 'undefined' ? navigator : undefined
  return {
    caches: 'caches' in deps ? deps.caches ?? null : g.caches ?? null,
    fetch: deps.fetch ?? ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init)),
    estimate: 'estimate' in deps
      ? deps.estimate ?? null
      : (nav?.storage?.estimate ? () => nav.storage.estimate() : null),
    isOnline: deps.isOnline ?? (() => !(nav && nav.onLine === false)),
    beginWriter: deps.beginWriter ?? registryBeginWriter,
    mayWrite: deps.mayWrite ?? mirrorMayWrite,
    readFence: deps.readFence ?? readStorageFence,
    readIndex: deps.readIndex ?? readDeviceIndex,
    requestRun: deps.requestRun ?? (() => requestSync(TRIGGER.enqueued)),
    sleep: deps.sleep ?? ((ms: number) => new Promise<void>(r => setTimeout(r, ms))),
    now: deps.now ?? Date.now,
    jitter: deps.jitter ?? Math.random,
  }
}
type Resolved = ReturnType<typeof resolve>

// --- the pin record ----------------------------------------------------------

function normalize(raw: unknown): PinRecord {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Partial<PinRecord>
  const ok = o.schema_version === PIN_SCHEMA && Array.isArray(o.urls)
  return {
    schema_version: PIN_SCHEMA,
    urls: ok ? o.urls!.filter((u): u is string => typeof u === 'string') : [],
    paused: ok && o.paused === true,
    cancel_gen: ok && typeof o.cancel_gen === 'number' ? o.cancel_gen : 0,
    retry_at: ok && typeof o.retry_at === 'number' ? o.retry_at : 0,
    backoff_n: ok && typeof o.backoff_n === 'number' ? o.backoff_n : 0,
  }
}

/** The owner's pin record (a read: unfenced, offline_owner only). */
export async function readPinRecord(ownerKey: string): Promise<PinRecord> {
  return normalize(await ownerGet(ownerKey, STORE.meta, META_IMAGES).catch(() => null))
}

/**
 * One fenced read-modify-write of the pin record. `edit` returns the next
 * record, or null for no change. Resolves the record as it now stands, or
 * null when refused (the C3 fence moved: nothing was written).
 */
async function editPinRecord(ctx: WriterCtx, edit: (cur: PinRecord) => PinRecord | null): Promise<PinRecord | null> {
  let out: PinRecord | null = null
  const r = await fencedWrite(ctx, [STORE.meta], (tx) => {
    const st = tx.objectStore(STORE.meta)
    const g = st.get(META_IMAGES)
    g.onsuccess = () => {
      const cur = normalize(g.result)
      const next = edit(cur)
      out = next ?? cur
      if (next) st.put(next, META_IMAGES)
    }
  })
  return r === 'ok' ? out : null
}

// --- the C3 gate and cache opens --------------------------------------------------

function gateOpen(h: WriterHandle, d: Resolved): boolean {
  return !h.signal.aborted && d.mayWrite(h.ctx)
}

/** The authoritative gate, read from the owner's storage fence. */
async function fenceHoldsFor(ctx: WriterCtx, d: Resolved): Promise<boolean> {
  if (!d.mayWrite(ctx)) return false
  const f = await d.readFence(ctx.ownerKey).catch(() => null)
  return !!f && f.state === 'open' && f.owner_key === ctx.ownerKey &&
    f.owner_incarnation === ctx.incarnation && f.fence_epoch === ctx.fenceEpoch && d.mayWrite(ctx)
}

async function fenceHolds(h: WriterHandle, d: Resolved): Promise<boolean> {
  return !h.signal.aborted && (await fenceHoldsFor(h.ctx, d)) && !h.signal.aborted
}

/** This incarnation of the owner is over (wiped, or the device moved on). */
async function incarnationGone(ctx: WriterCtx, d: Resolved): Promise<boolean> {
  const f = await d.readFence(ctx.ownerKey).catch(() => null)
  if (!f || f.owner_incarnation !== ctx.incarnation) return true
  const idx = await d.readIndex().catch(() => null)
  if (!idx) return false
  return idx.offline_owner !== ctx.ownerKey || incarnationOf(idx, ctx.ownerKey) !== ctx.incarnation ||
    idx.pending_wipes.some(t => t.owner_key === ctx.ownerKey)
}

interface LocksLike { request: (name: string, opts: { mode: 'shared' }, cb: () => Promise<unknown>) => Promise<unknown> }

function withSharedOwnerLock<T>(fn: () => Promise<T>): Promise<T> {
  const l = typeof navigator !== 'undefined' ? (navigator as Navigator & { locks?: LocksLike }).locks : undefined
  return l && typeof l.request === 'function' ? (l.request(LOCK_OWNER_WRITE, { mode: 'shared' }, fn) as Promise<T>) : fn()
}

/**
 * Open this incarnation's cache, or null. Inside the shared cb-owner-write
 * lock; only with the gate open; only an EXISTING cache unless `create`;
 * validated against the storage fence after the open on every path, and a
 * stale open whose incarnation is gone deletes the cache it may have made.
 */
async function openOwnerCache(ctx: WriterCtx, d: Resolved, create: boolean, live: () => boolean = () => true): Promise<Cache | null> {
  const caches = d.caches
  if (!caches) return null
  const name = ownerImageCacheName(ctx.ownerKey, ctx.incarnation)
  return withSharedOwnerLock(async () => {
    if (!live() || !d.mayWrite(ctx)) return null
    if (!create && !(await caches.has(name))) return null
    if (!live() || !d.mayWrite(ctx)) return null
    const cache = await caches.open(name)
    if (live() && (await fenceHoldsFor(ctx, d))) return cache
    if (await incarnationGone(ctx, d)) await caches.delete(name).catch(() => false)
    return null
  })
}

async function storedUrls(cache: Cache): Promise<Set<string>> {
  const out = new Set<string>()
  for (const req of await cache.keys()) {
    if (req.url.startsWith(`https://${IMAGE_HOST}/`)) out.add(req.url)
  }
  return out
}

/** Delete `url` only when the entry is still the one `runId` stored. */
async function deleteOwnEntry(cache: Cache, url: string, runId: string): Promise<void> {
  try {
    const hit = await cache.match(url)
    if (hit && hit.headers.get(RUN_HEADER) === runId) await cache.delete(url)
  } catch { /* the orphan sweep or the next wipe */ }
}

// --- live status -------------------------------------------------------------

const IDLE: ImageDownloadStatus = { ownerKey: null, phase: 'idle', total: 0, done: 0, failed: 0, bytes: 0, error: null, retryAfterMs: null }
let status: ImageDownloadStatus = IDLE
const listeners = new Set<() => void>()

function setStatus(next: Partial<ImageDownloadStatus>): void {
  status = { ...status, ...next }
  for (const l of [...listeners]) l()
}

export function getImageDownloadStatus(): ImageDownloadStatus {
  return status
}

export function subscribeImageDownload(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

export function useImageDownload(): ImageDownloadStatus {
  return useSyncExternalStore(subscribeImageDownload, getImageDownloadStatus, getImageDownloadStatus)
}

export function c6FromStatus(s: ImageDownloadStatus): C6ImagesState {
  if (s.phase === 'downloading') {
    const percent = s.total ? Math.min(100, Math.floor(((s.done + s.failed) / s.total) * 100)) : 0
    return { state: 'downloading', percent }
  }
  if (s.phase === 'error' && s.error) return { state: 'error', code: s.error }
  if (s.phase === 'done' && s.failed === 0) return { state: 'ready' }
  if (s.phase === 'idle') return { state: 'not_started' }
  return { state: 'error', code: 'incomplete' }
}

// --- scheduler-facing state ----------------------------------------------------

/** Missing pinned images wait for a run (the runtime's hasPendingWork). */
let pending = false
/** URLs that failed permanently in this page's life (skipped until pinned again). */
const failedUrls = new Set<string>()

export function imagesPendingSync(): boolean {
  return pending
}

// --- the run -----------------------------------------------------------------

interface Job {
  ownerKey: string
  runId: string
  cancelGen: number
  cancelled: boolean
  queue: string[]
  working: Set<string>
  inflight: Map<string, AbortController>
  dropped: Set<string>
  done: Promise<RunOutcome>
  stop: (e: ImageDownloadError | null, retryAfterMs?: number | null) => void
}

type RunOutcome = { kind: 'complete' } | { kind: 'cancelled' } | { kind: 'error'; error: ImageDownloadError; retryAfterMs: number | null }

let job: Job | null = null
let runSeq = 0

function isQuotaError(e: unknown): boolean {
  return !!e && typeof e === 'object' && 'name' in e &&
    ((e as { name: string }).name === 'QuotaExceededError' || (e as { code?: number }).code === 22)
}

function retryAfterMs(res: Response, now: number): number | null {
  const raw = res.headers.get('retry-after')
  if (!raw) return null
  const secs = Number(raw)
  if (Number.isFinite(secs)) return Math.max(0, secs * 1000)
  const at = Date.parse(raw)
  return Number.isFinite(at) ? Math.max(0, at - now) : null
}

const TRANSIENT_STATUS = new Set([408, 425, 429, 500, 502, 503, 504])

async function runJob(j: Job, h: WriterHandle, d: Resolved, cache: Cache): Promise<RunOutcome> {
  let stopError: ImageDownloadError | null = null
  let stopRetryAfter: number | null = null
  j.stop = (e, after = null) => {
    if (e && !stopError) { stopError = e; stopRetryAfter = after }
    j.cancelled = true
    for (const c of j.inflight.values()) c.abort()
  }
  // C3 step 2 (and suspension) aborts the registration: the run stops now.
  const onAbort = () => j.stop('fenced')
  h.signal.addEventListener('abort', onAbort)
  const transient = (res: Response | null) => j.stop(d.isOnline() ? 'server_error' : 'offline', res ? retryAfterMs(res, d.now()) : null)

  /**
   * The persisted record still wants `url` from THIS run: not paused, no
   * Cancel or Remove since the run started (in any tab), and still pinned.
   * 'stop' ends the run; 'skip' drops only this URL.
   */
  const stillWanted = async (url: string): Promise<'ok' | 'skip' | 'stop'> => {
    const rec = await readPinRecord(j.ownerKey)
    if (rec.paused || rec.cancel_gen !== j.cancelGen) return 'stop'
    return rec.urls.includes(url) ? 'ok' : 'skip'
  }

  const one = async (url: string): Promise<void> => {
    if (j.cancelled || j.dropped.has(url)) return
    // Re-read before every request: a Cancel in another tab stops this run.
    const before = await stillWanted(url)
    if (before === 'stop') { j.stop(null); return }
    if (before === 'skip' || j.cancelled || j.dropped.has(url)) return
    if (!d.isOnline()) { j.stop('offline'); return }
    const controller = new AbortController()
    j.inflight.set(url, controller)
    const timer = setTimeout(() => controller.abort(), IMAGE_TIMEOUT_MS)
    let res: Response | null = null
    let blob: Blob | null = null
    try {
      try {
        res = await d.fetch(url, { mode: 'cors', credentials: 'omit', cache: 'no-store', signal: controller.signal })
      } catch {
        res = null
      }
      if (j.cancelled || j.dropped.has(url)) return
      if (!res || TRANSIENT_STATUS.has(res.status)) { transient(res); return }
      if (!res.ok || (res.type !== 'cors' && res.type !== 'basic' && res.type !== 'default')) {
        failedUrls.add(url)
        setStatus({ failed: status.failed + 1 })
        return
      }
      try {
        blob = await res.blob()
      } catch {
        // A reset or a timeout while the body streams: a transport failure.
        if (j.cancelled || j.dropped.has(url)) return
        transient(null)
        return
      }
    } finally {
      clearTimeout(timer)
      j.inflight.delete(url)
    }
    if (j.cancelled || j.dropped.has(url)) return
    if (!gateOpen(h, d)) { j.stop('fenced'); return }
    try {
      await cache.put(url, new Response(blob, {
        status: 200,
        headers: { 'content-type': res.headers.get('content-type') || 'image/jpeg', [RUN_HEADER]: j.runId },
      }))
    } catch (e) {
      if (isQuotaError(e)) { j.stop('storage_full'); return }
      failedUrls.add(url)
      setStatus({ failed: status.failed + 1 })
      return
    }
    // A put cannot be aborted: the storage fence decides whether it stays.
    if (!(await fenceHolds(h, d))) {
      await deleteOwnEntry(cache, url, j.runId)
      if (await incarnationGone(h.ctx, d)) await d.caches?.delete(ownerImageCacheName(h.ctx.ownerKey, h.ctx.incarnation)).catch(() => false)
      j.stop('fenced')
      return
    }
    // And the record, before keeping it: a Cancel, Remove or unpin in any tab.
    const after = await stillWanted(url)
    if (after !== 'ok' || j.dropped.has(url)) {
      await deleteOwnEntry(cache, url, j.runId)
      if (after === 'stop') j.stop(null)
      return
    }
    setStatus({ done: status.done + 1, bytes: status.bytes + blob.size })
  }

  const lane = async () => {
    while (!j.cancelled) {
      const url = j.queue.shift()
      if (url === undefined) return
      j.working.add(url)
      try { await one(url) } finally { j.working.delete(url) }
    }
  }
  try {
    await Promise.all(Array.from({ length: MAX_CONCURRENCY }, lane))
  } finally {
    h.signal.removeEventListener('abort', onAbort)
  }
  if (stopError) return { kind: 'error', error: stopError, retryAfterMs: stopRetryAfter }
  return j.cancelled ? { kind: 'cancelled' } : { kind: 'complete' }
}

/**
 * The scheduler's image step (C1b run order step 4, the last one; the
 * scheduler runs its steps under one browser-wide lock). Reads the persisted
 * record: nothing while paused or before its Retry-After deadline.
 * Downloads what is pinned and missing; a transient stop persists the next
 * deadline. Resolves when the run ends; never throws.
 */
export async function runImageStep(deps: ImageDeps = {}): Promise<void> {
  const d = resolve(deps)
  if (job || !d.caches) return
  const h = d.beginWriter()
  if (!h) return
  try {
    const rec = await readPinRecord(h.ctx.ownerKey)
    if (rec.paused || rec.urls.length === 0) { pending = false; return }
    if (d.now() < rec.retry_at) { pending = true; return }
    const live = () => gateOpen(h, d)
    let cache = await openOwnerCache(h.ctx, d, false, live)
    const have = cache ? await storedUrls(cache) : new Set<string>()
    const missing = rec.urls.filter(u => !have.has(u) && !failedUrls.has(u))
    if (missing.length === 0) { pending = false; return }
    cache ??= await openOwnerCache(h.ctx, d, true, live)
    if (!cache || job || !gateOpen(h, d)) return
    const j: Job = {
      ownerKey: h.ctx.ownerKey, runId: `${Date.now().toString(36)}-${++runSeq}-${Math.random().toString(36).slice(2, 8)}`,
      cancelGen: rec.cancel_gen, cancelled: false, queue: [...missing], working: new Set(), inflight: new Map(),
      dropped: new Set(), done: Promise.resolve({ kind: 'complete' }), stop: () => {},
    }
    job = j
    status = { ...IDLE, ownerKey: h.ctx.ownerKey, phase: 'downloading', total: missing.length }
    for (const l of [...listeners]) l()
    j.done = runJob(j, h, d, cache)
    const out = await j.done
    if (job === j) job = null
    if (out.kind === 'error') {
      if (out.error === 'server_error' || out.error === 'offline') {
        // Persisted, so every tab's step honours it; never shortened.
        const now = d.now()
        const jitter = d.jitter()
        await editPinRecord(h.ctx, (cur) => {
          const n = cur.backoff_n + 1
          const due = now + backoffDelayMs(n, jitter, out.retryAfterMs === null ? null : out.retryAfterMs / 1000)
          return { ...cur, backoff_n: n, retry_at: Math.max(cur.retry_at, due) }
        }).catch(() => null)
      }
      if (out.error === 'storage_full') pending = false
      setStatus({ phase: 'error', error: out.error, retryAfterMs: out.retryAfterMs })
    } else if (out.kind === 'cancelled') {
      setStatus({ phase: 'cancelled' })
    } else {
      await editPinRecord(h.ctx, (cur) => (cur.backoff_n === 0 && cur.retry_at === 0 ? null : { ...cur, backoff_n: 0, retry_at: 0 })).catch(() => null)
      pending = false
      setStatus({ phase: 'done' })
    }
  } catch {
    // The run's own failure is its own; the scheduler goes on.
  } finally {
    h.end()
  }
}

/**
 * Pin `urls` (any cards.scryfall.io image URL, rewritten to `size`) for the
 * owner this tab holds, then ask the scheduler for a run. Clears a pause;
 * never touches the backoff deadline. `signal` (the user's Cancel) is checked
 * after every await and inside the write transaction itself.
 */
export async function pinImages(
  req: { ownerKey: string; urls: readonly string[]; size: ImageSize; signal?: AbortSignal },
  deps: ImageDeps = {},
): Promise<PinResult> {
  const d = resolve(deps)
  if (!d.caches) return { ok: false, reason: 'unsupported' }
  if (!d.isOnline()) return { ok: false, reason: 'offline' }
  if (req.signal?.aborted) return { ok: false, reason: 'cancelled' }
  const h = d.beginWriter()
  if (!h || h.ctx.ownerKey !== req.ownerKey) { h?.end(); return { ok: false, reason: 'fenced' } }
  const cancelled = () => !!req.signal?.aborted
  try {
    const wanted = [...new Set(req.urls.map(u => imageUrlAtSize(u, req.size)).filter((u): u is string => !!u))]
    if (!gateOpen(h, d)) return { ok: false, reason: 'fenced' }
    const cache = await openOwnerCache(h.ctx, d, false, () => gateOpen(h, d))
    const have = cache ? await storedUrls(cache) : new Set<string>()
    if (cancelled()) return { ok: false, reason: 'cancelled' }
    const todo = wanted.filter(u => !have.has(u))
    const estimatedBytes = estimateBytes(todo.length, req.size)
    if (todo.length && d.estimate) {
      try {
        const { usage = 0, quota = 0 } = await d.estimate()
        if (quota > 0 && usage + estimatedBytes > quota * QUOTA_HEADROOM) return { ok: false, reason: 'storage_full', estimatedBytes }
      } catch {
        // No estimate: go ahead; a QuotaExceededError still stops the run.
      }
    }
    if (cancelled()) return { ok: false, reason: 'cancelled' }
    if (!wanted.length) return { ok: true, queued: 0, alreadyStored: 0, estimatedBytes: 0 }
    let wasCancelled = false
    const rec = await editPinRecord(h.ctx, (cur) => {
      if (cancelled()) { wasCancelled = true; return null }
      return { ...cur, urls: [...new Set([...cur.urls, ...wanted])], paused: false }
    })
    if (wasCancelled) return { ok: false, reason: 'cancelled' }
    if (!rec) return { ok: false, reason: 'fenced' }
    for (const u of wanted) failedUrls.delete(u)
    pending = todo.length > 0
    if (todo.length > 0 && !cancelled()) d.requestRun()
    return { ok: true, queued: todo.length, alreadyStored: wanted.length - todo.length, estimatedBytes }
  } catch (e) {
    return { ok: false, reason: isQuotaError(e) ? 'storage_full' : 'unsupported' }
  } finally {
    h.end()
  }
}

/**
 * The user's Cancel: a durable pause plus a new Cancel generation in the
 * record (so a downloader in ANY tab stops before its next request and drops
 * a put it has not kept yet), and this tab's run stopped at once.
 */
export async function cancelImageDownload(ownerKey: string, deps: ImageDeps = {}): Promise<void> {
  const d = resolve(deps)
  const j = job && job.ownerKey === ownerKey ? job : null
  if (j) j.stop(null)
  pending = false
  const h = d.beginWriter()
  if (h && h.ctx.ownerKey === ownerKey) {
    try {
      await editPinRecord(h.ctx, (cur) => ({ ...cur, paused: true, cancel_gen: cur.cancel_gen + 1 }))
    } catch { /* this tab's run is stopped either way */ } finally {
      h.end()
    }
  } else {
    h?.end()
  }
  if (j) await Promise.race([j.done, d.sleep(STOP_WAIT_MS)])
}

/** Test seam: wait for the run in flight. */
export async function whenImageRunSettled(): Promise<void> {
  if (job) await job.done
}

/** The persisted C6 `images` item for the owner this tab holds. */
export async function readOwnerImageStatus(ownerKey: string, deps: ImageDeps = {}): Promise<OwnerImageStatus> {
  const d = resolve(deps)
  const ctx = currentWriterCtx()
  const live = job && job.ownerKey === ownerKey ? c6FromStatus(status) : null
  const none: OwnerImageStatus = { pinned: 0, stored: 0, paused: false, c6: live ?? { state: 'not_started' } }
  if (!ctx || ctx.ownerKey !== ownerKey || !d.caches) return none
  const rec = await readPinRecord(ownerKey)
  const cache = await openOwnerCache(ctx, d, false)
  const have = cache ? await storedUrls(cache) : new Set<string>()
  const stored = rec.urls.filter(u => have.has(u)).length
  let c6: C6ImagesState
  if (live) c6 = live
  else if (rec.urls.length === 0) c6 = { state: 'not_started' }
  else if (stored === rec.urls.length) c6 = { state: 'ready' }
  else if (status.ownerKey === ownerKey && status.phase === 'error' && status.error) c6 = { state: 'error', code: status.error }
  else c6 = { state: 'error', code: 'incomplete' }
  return { pinned: rec.urls.length, stored, paused: rec.paused, c6 }
}

/** Prime the scheduler's pending flag from the durable record (boot). */
export async function primeImagePending(deps: ImageDeps = {}): Promise<boolean> {
  const ctx = currentWriterCtx()
  if (!ctx) { pending = false; return false }
  const s = await readOwnerImageStatus(ctx.ownerKey, deps).catch(() => null)
  pending = !!s && !s.paused && s.stored < s.pinned
  return pending
}

/**
 * Unpin these images: out of the record (fenced), out of this tab's run (a
 * downloader in another tab drops them at its next record check), and their
 * stored entries deleted.
 */
export async function unpinImages(ownerKey: string, urls: readonly string[], deps: ImageDeps = {}): Promise<number> {
  const d = resolve(deps)
  if (!d.caches) return 0
  const drop = new Set<string>()
  for (const u of urls) {
    drop.add(u)
    for (const size of ['small', 'normal'] as const) {
      const at = imageUrlAtSize(u, size)
      if (at) drop.add(at)
    }
  }
  const running = job && job.ownerKey === ownerKey ? job : null
  if (running) {
    let leaving = 0
    running.queue = running.queue.filter(u => {
      if (!drop.has(u)) return true
      leaving += 1
      return false
    })
    for (const u of drop) {
      running.inflight.get(u)?.abort()
      if (running.working.has(u) && !running.dropped.has(u)) leaving += 1
      running.dropped.add(u)
    }
    if (leaving) setStatus({ total: Math.max(0, status.total - leaving) })
  }
  const h = d.beginWriter()
  if (!h || h.ctx.ownerKey !== ownerKey) { h?.end(); return 0 }
  try {
    const rec = await editPinRecord(h.ctx, (cur) => {
      const keep = cur.urls.filter(u => !drop.has(u))
      return keep.length === cur.urls.length ? null : { ...cur, urls: keep }
    })
    if (!rec) return 0
    const cache = await openOwnerCache(h.ctx, d, false, () => gateOpen(h, d))
    if (!cache) return 0
    let removed = 0
    for (const u of drop) {
      if (!gateOpen(h, d)) break
      if (await cache.delete(u)) removed += 1
    }
    return removed
  } finally {
    h.end()
  }
}

/**
 * The user's Remove: clear the record (fenced, a new Cancel generation, so
 * every tab's downloader stops and drops what it has not kept), stop this
 * tab's run, delete this incarnation's cache and verify it is gone.
 */
export async function removeAllImages(ownerKey: string, deps: ImageDeps = {}): Promise<boolean> {
  const d = resolve(deps)
  pending = false
  const j = job && job.ownerKey === ownerKey ? job : null
  if (j) j.stop(null)
  const h = d.beginWriter()
  if (!h || h.ctx.ownerKey !== ownerKey || !d.caches) { h?.end(); return !d.caches }
  try {
    const rec = await editPinRecord(h.ctx, (cur) => ({ ...cur, urls: [], paused: false, cancel_gen: cur.cancel_gen + 1 }))
    if (!rec) return false
    if (j) await Promise.race([j.done, d.sleep(STOP_WAIT_MS)])
    if (status.ownerKey === ownerKey) setStatus({ ...IDLE })
    const name = ownerImageCacheName(h.ctx.ownerKey, h.ctx.incarnation)
    return await withSharedOwnerLock(async () => {
      if (!gateOpen(h, d)) return false
      await d.caches!.delete(name)
      return !(await d.caches!.has(name))
    })
  } catch {
    return false
  } finally {
    h.end()
  }
}

/** Test seam. */
export function __resetOfflineImagesForTests(): void {
  if (job) job.cancelled = true
  job = null
  status = IDLE
  listeners.clear()
  pending = false
  failedUrls.clear()
  runSeq = 0
}
